import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useAuthStore } from '@/features/auth';
import { useAppBranding } from '@/hooks/useAppBranding';

/* ─── Greeting ─────────────────────────────────────────────────── */

function getGreetingKey(): string {
  const h = new Date().getHours();
  if (h < 6) return 'home.greeting.night';
  if (h < 12) return 'home.greeting.morning';
  if (h < 17) return 'home.greeting.afternoon';
  if (h < 21) return 'home.greeting.evening';
  return 'home.greeting.night';
}

/* ─── Wavy bottom clip-path (single-surface, no seam) ──────────── */

function HeroWaveClipDef() {
  return (
    <svg width="0" height="0" style={{ position: 'absolute' }}>
      <defs>
        <clipPath id="heroWaveClip" clipPathUnits="objectBoundingBox">
          <path d="M0 0 H1 V1 H0 Z" />
        </clipPath>
      </defs>
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   HomeTopArea — Hero gradient + left text + right illustration

   Props (all optional — defaults to greeting + home-hero.png):
   - leftContent:  Custom ReactNode for the left text area
   - imageSrc:     Custom image path (default: /images/home-hero.png)
   ═══════════════════════════════════════════════════════════════════ */

interface HomeTopAreaProps {
  leftContent?: ReactNode;
  imageSrc?: string;
  showLeftIllustration?: boolean;
  showRightIllustration?: boolean;
  showTeamImage?: boolean;
  showGreeting?: boolean;
  slogan?: string;
}

export function HomeTopArea({ leftContent, imageSrc = '/images/home-hero.png', showLeftIllustration = true, showRightIllustration = true, showTeamImage = true, showGreeting = true, slogan }: HomeTopAreaProps) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const user = useAuthStore((s) => s.user);
  const { companyName } = useAppBranding();

  const logoBaseFilter = 'drop-shadow(0 1px 0 rgba(255,255,255,0.5)) drop-shadow(0 2px 6px rgba(0,0,0,0.2))';

  /* Default left content: greeting + name + role */
  const defaultLeftContent = (() => {
    const greetingKey = getGreetingKey();
    const fullName = (() => {
      if (!user) return '';
      if (i18n.language === 'he' && user.firstNameHe) {
        return `${user.firstNameHe} ${user.lastNameHe || user.lastName}`;
      }
      return `${user.firstName} ${user.lastName}`;
    })();
    return (
      <>
        <h1 className="greeting-title" dir="auto" style={{ transform: `translateX(${isRtl ? '12px' : '-12px'})` }}>
          {t(greetingKey)},
        </h1>
        <span className="greeting-title font-extrabold">
          {fullName}
        </span>
      </>
    );
  })();

  return (
    <>
      <HeroWaveClipDef />
      {/* ── Hero gradient (always LTR layout) ────────────── */}
      <div
        dir="ltr"
        className="relative z-10 overflow-hidden text-white"
        style={{
          minHeight: 400,
          background: 'linear-gradient(180deg, #0d2b6b 0%, #1a4fa0 40%, #3b8dd4 75%, #6bb4e8 100%)',
          clipPath: 'url(#heroWaveClip)',
          paddingBottom: 30,
        }}
      >
        {/* Logo — opposite side from greeting (RTL-aware) */}
        {!(slogan && !showLeftIllustration && !showRightIllustration) && (
          <div
            className="absolute top-7 z-10 h-14 animate-logo-brighten"
            style={{
              [isRtl ? 'left' : 'right']: 16,
              filter: logoBaseFilter,
            }}
          >
            {/* Glass logo — gradient masked by logo shape */}
            <div
              className="h-full aspect-[3/2]"
              role="img"
              aria-label={companyName}
              style={{
                background: 'linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(200,220,255,0.7) 45%, rgba(255,255,255,0.85) 55%, rgba(180,200,240,0.6) 100%)',
                maskImage: 'url(/images/brainsway-logo-white.png)',
                WebkitMaskImage: 'url(/images/brainsway-logo-white.png)',
                maskSize: 'contain',
                WebkitMaskSize: 'contain',
                maskRepeat: 'no-repeat',
                WebkitMaskRepeat: 'no-repeat',
                maskPosition: 'center',
                WebkitMaskPosition: 'center',
              }}
            />
          </div>
        )}

        {/* Illustrations row — left + center text + right */}
        <div className={`relative z-10 flex items-end justify-between ${slogan && !showLeftIllustration && !showRightIllustration ? 'pb-0' : !showGreeting && !showTeamImage ? 'pb-2' : 'pb-10'}`} style={{ marginTop: showTeamImage && !showLeftIllustration && !showRightIllustration ? '0px' : slogan && !showLeftIllustration && !showRightIllustration ? '0px' : '60px' }}>
          {/* Center: team image + greeting text stacked */}
          <div className={`${(showLeftIllustration && !slogan) || showRightIllustration ? 'absolute inset-x-0 bottom-0' : 'relative w-full'} z-20 flex flex-col ${showTeamImage || slogan ? 'items-center justify-center' : showGreeting ? (isRtl ? 'items-end pr-6' : 'items-start pl-6') + ' pb-44' : ''} ${!showTeamImage && !showGreeting && !slogan ? 'pointer-events-none' : ''}`}>
            {showTeamImage && (
              <>
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.1, ease: 'easeOut' }}
                >
                  <img
                    src="/images/home-hero-team.png"
                    alt=""
                    className="h-[180px] sm:h-[240px] md:h-[320px] lg:h-[400px] w-auto object-contain"
                    draggable={false}
                  />
                </motion.div>
                <div className="flex flex-col items-center -mt-10 pb-6">
                  <span className="text-[24px] sm:text-[30px] md:text-[36px] font-extrabold tracking-wide text-black">{companyName}</span>
                  <span className="text-[12px] sm:text-[14px] md:text-[16px] font-bold uppercase tracking-[0.25em] text-white/70">Leave No Patient Behind</span>
                </div>
              </>
            )}
            {showGreeting && (
              <motion.div
                className={`flex flex-col ${showTeamImage ? 'items-center text-center' : isRtl ? 'items-end text-right' : 'items-start text-left'} px-6 -mt-2`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                {leftContent ?? defaultLeftContent}
              </motion.div>
            )}
            {slogan && (
              <>
                {/* Illustration above slogan */}
                {showLeftIllustration && (
                  <motion.div
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
                  >
                    <img
                      src="/images/home-hero-left.png"
                      alt=""
                      className="relative z-10 h-[140px] sm:h-[160px] md:h-[180px] w-auto object-contain drop-shadow-lg"
                      draggable={false}
                    />
                  </motion.div>
                )}
                <motion.div
                  className="pointer-events-none flex flex-col items-center mt-14 space-y-1 pb-20"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
                >
                  <span className="text-[13px] sm:text-[15px] md:text-[17px] font-light tracking-[0.15em] uppercase text-white/50">To create a world where</span>
                  <span className="text-[18px] sm:text-[22px] md:text-[26px] font-bold tracking-wide text-white">mental health</span>
                  <span className="text-[15px] sm:text-[18px] md:text-[20px] font-extralight italic tracking-[0.2em] text-white/70">is within reach for all.</span>
                </motion.div>
              </>
            )}
          </div>

          {/* Left illustration (skip if rendered inside slogan above) */}
          {showLeftIllustration && !slogan ? (
            <motion.div
              className="pl-10 pt-2"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: 'easeOut' }}
            >
              <div
                className="relative"
                style={{ transform: 'translateY(-16px)' }}
              >
                <img
                  src="/images/home-hero-left.png"
                  alt=""
                  className="relative z-10 h-[240px] w-auto object-contain drop-shadow-lg"
                  draggable={false}
                />
              </div>
            </motion.div>
          ) : <div />}

          {/* Right illustration */}
          {showRightIllustration ? (
            <motion.div
              className="-mr-5 pt-2"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3, ease: 'easeOut' }}
            >
              <div
                className="relative z-30"
                style={{ transform: 'translateY(55px)' }}
              >
                <img
                  src={imageSrc}
                  alt=""
                  className="relative z-10 h-[180px] w-auto object-contain drop-shadow-lg"
                  draggable={false}
                  data-home-hero
                />
                {/* Gray floor beneath illustration */}
                <div
                  className="absolute bottom-0 left-1/2 -translate-x-1/2 z-0"
                  style={{
                    width: '130%',
                    height: '28px',
                    background: 'linear-gradient(180deg, rgba(200,210,225,0.15) 0%, rgba(190,200,215,0.35) 100%), radial-gradient(ellipse at center 30%, rgba(220,225,235,0.4) 0%, transparent 70%)',
                    borderTop: '1px solid rgba(180,200,230,0.5)',
                    boxShadow: '0 -2px 8px rgba(30,80,180,0.2)',
                    borderRadius: '50%',
                  }}
                />
              </div>
            </motion.div>
          ) : <div />}
        </div>

      </div>
    </>
  );
}
